/**
 * Guide Me - root component mounted once inside LayoutShell.
 *
 * Resolves the active plugin + path, renders the dismissible hint pill, and
 * orchestrates three interactive surfaces:
 *
 *  1. HintPill       - the floating launcher (idle).
 *  2. GuideMenuPanel - sidebar listing one card per authored tour for the
 *                      current plugin, plus inline HTML guide reader.
 *  3. TourPlayer     - plays the selected tour with a smart narration bubble.
 *                      TourLogPanel accumulates the step history alongside it.
 *
 * Loading strategy is **intent-driven** (see ./tourCatalog.ts):
 *   • Trigger #1 — route activation: kick a fire-and-forget preload of every
 *                  tour body that belongs to the now-active plugin so the menu
 *                  has localized titles and step counts ready when opened.
 *   • Trigger #2 — menu open: explicitly preload again (idempotent) and
 *                  re-render once the cache settles.
 *   • Trigger #3 — play click: await the tour body + locale overlay before
 *                  transitioning into the player so it never sees a partial
 *                  Tour object.
 *
 * "Ask the guide" (Phase 2 AI agent) is temporarily hidden from all surfaces.
 * The backend scaffolding is intact — remove the comments below to re-enable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyLocale, pickTourLocaleKey } from '@empowernow/etl-tour';
import { useGuideMeAdapter, useGuideMeFloatingLauncher } from './context';
import { HintPill, ensureHintPillStyles } from './HintPill';
import { TourPlayer } from './TourPlayer';
import { GuideMenuPanel, ensureGuideMeMenuStyles } from './GuideMenuPanel';
import { TourLogPanel } from './TourLogPanel';
import type { Tour, TourLogEntry, TourSummary } from './types';

// Note: locator vocabularies are registered by the host before mounting
// `<GuideMeRoot>`. The package no longer side-effect-imports a host
// vocabulary index — that coupling lived in this file's previous form.

type Mode =
  | { kind: 'idle' }
  | { kind: 'menu' }
  | { kind: 'loading'; tourId: string }
  | { kind: 'playing'; tour: Tour };

// ── Deep-link auto-start plumbing ──
// A `?tour=<id>` deep-link (the partner-training "Show me in Experience" links)
// is captured at startup by captureTourParam.ts into BOTH sessionStorage (fast
// same-tab path) and localStorage (survives the OIDC login round-trip and any
// bootstrap reloads, which sessionStorage does not always do on a fresh/incognito
// session). We read either, preferring sessionStorage, and honour a localStorage
// pointer only while it is fresh so a stale link can't silently fire a tour later.
const PENDING_TOUR_KEY = 'guide-me:pending-tour';
const PENDING_TOUR_TS_KEY = 'guide-me:pending-tour:ts';
const PENDING_TOUR_MAX_AGE_MS = 10 * 60 * 1000;
// Survives React remounts during auth/plugin bootstrap churn (a full page reload
// resets it, which is correct — the storage pointer is then re-consumed fresh).
let deepLinkTourInFlight: string | null = null;

function readPendingTourId(): string | null {
  try {
    const ss = window.sessionStorage.getItem(PENDING_TOUR_KEY);
    if (ss) return ss;
  } catch { /* ignore */ }
  try {
    const ls = window.localStorage.getItem(PENDING_TOUR_KEY);
    if (ls) {
      const ts = Number(window.localStorage.getItem(PENDING_TOUR_TS_KEY) ?? '0');
      if (!ts || Date.now() - ts <= PENDING_TOUR_MAX_AGE_MS) return ls;
      window.localStorage.removeItem(PENDING_TOUR_KEY);
      window.localStorage.removeItem(PENDING_TOUR_TS_KEY);
    }
  } catch { /* ignore */ }
  try {
    return new URLSearchParams(window.location.search).get('tour');
  } catch { /* ignore */ }
  return null;
}

function clearPendingTourId(): void {
  try { window.sessionStorage.removeItem(PENDING_TOUR_KEY); } catch { /* ignore */ }
  try {
    window.localStorage.removeItem(PENDING_TOUR_KEY);
    window.localStorage.removeItem(PENDING_TOUR_TS_KEY);
  } catch { /* ignore */ }
}

export function GuideMeRoot() {
  const adapter = useGuideMeAdapter();
  const { dismissFloatingLauncher, floatingLauncherDismissed } = useGuideMeFloatingLauncher();
  const { pluginId, pathname, routeKey, resolvedLocale } = adapter;
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });

  /* ---- Tour log state ---- */
  const [logEntries, setLogEntries] = useState<TourLogEntry[]>([]);
  const logEntriesRef = useRef<TourLogEntry[]>([]);

  useEffect(() => {
    ensureHintPillStyles();
    ensureGuideMeMenuStyles();
  }, []);

  const guideEntry = useMemo(() => adapter.getGuideEntry(pluginId), [adapter, pluginId]);

  /* ─────────────────── lazy catalog wiring ─────────────────── */

  /**
   * Bumped each time the lazy preload settles. We use it as a `useMemo`
   * dependency so the menu cards re-render with localized titles + step
   * counts the moment bodies finish parsing in the background.
   */
  const [cacheVersion, setCacheVersion] = useState(0);

  // Trigger #1: route active → fire-and-forget preload. Never blocks paint.
  useEffect(() => {
    adapter.preloadPluginSilent(pluginId, resolvedLocale);
  }, [adapter, pluginId, resolvedLocale]);

  // Track when the preload settles so the menu can refresh.
  useEffect(() => {
    let cancelled = false;
    adapter.preloadPlugin(pluginId, resolvedLocale)
      .then(() => {
        if (!cancelled) setCacheVersion((v) => v + 1);
      })
      .catch(() => {
        // Already logged inside the host catalog; nothing to do here.
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, pluginId, resolvedLocale]);

  /**
   * Synchronous summaries — the menu and pill render from these even
   * before any body has been parsed. `cacheVersion` is in the dep list
   * purely to pick up the post-preload `stepCount` upgrade.
   */
  const tourSummaries = useMemo(
    () => adapter.getToursForPlugin(pluginId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adapter, pluginId, cacheVersion],
  );
  const pillSummary = useMemo(
    () => adapter.findTourFor(pluginId, pathname),
    [adapter, pluginId, pathname],
  );

  /* ─────────────────── locale-flip while playing ─────────────────── */

  const playingTourId = mode.kind === 'playing' ? mode.tour.id : null;
  useEffect(() => {
    if (!playingTourId) return;
    let cancelled = false;
    adapter.loadTourById(playingTourId, resolvedLocale)
      .then((merged) => {
        if (cancelled) return;
        setMode((m) =>
          m.kind === 'playing' && m.tour.id === playingTourId
            ? { kind: 'playing', tour: merged }
            : m,
        );
      })
      .catch(() => {
        /* keep current Tour on overlay-load failure */
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, resolvedLocale, playingTourId]);

  /**
   * Localized teasers shown in the orb's speech bubble — one per authored
   * tour for the active plugin, so the user previews everything they can
   * learn before opening the menu. The bubble cycles through them in a
   * typing-style animation.
   */
  const pillTeasers = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const teasers: string[] = [];
    for (const summary of tourSummaries) {
      const cached = adapter.getCachedTour(summary.id);
      const title = cached
        ? applyLocale(cached, pickTourLocaleKey(resolvedLocale, cached.locales)).title
        : summary.title;
      const trimmed = (title ?? '').trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      teasers.push(trimmed);
    }
    return teasers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, tourSummaries, resolvedLocale, cacheVersion]);

  const pluginGuideUrl = useMemo(
    () => adapter.getRouteGuideUrl(guideEntry, pathname),
    [adapter, guideEntry, pathname],
  );

  // Reset interactive state when the user genuinely changes context
  // (different plugin, or a route hop while sitting in the menu/idle).
  // A running tour drives navigation itself via `ctx.navigate`, so we must
  // NOT reset while `mode` is `loading` or `playing` — otherwise the tour's
  // own `navigate "/foo"` step unmounts the TourPlayer and the user sees
  // what looks like a page refresh that drops them back to the launcher.
  useEffect(() => {
    setMode((m) => (m.kind === 'playing' || m.kind === 'loading' ? m : { kind: 'idle' }));
  }, [pluginId, pathname]);

  /* ─────────────────── intent handlers ─────────────────── */

  const handleOpenMenu = useCallback(() => {
    setMode({ kind: 'menu' });
    setLogEntries([]);
    logEntriesRef.current = [];
    // Trigger #2: explicit preload on menu open. Idempotent — same
    // promises as trigger #1 just get awaited again.
    adapter.preloadPluginSilent(pluginId, resolvedLocale);
  }, [adapter, pluginId, resolvedLocale]);

  // Trigger #3: play click → await body + locale overlay, then transition.
  const handleSelectTour = useCallback(
    (summary: TourSummary) => {
      setLogEntries([]);
      logEntriesRef.current = [];
      setMode({ kind: 'loading', tourId: summary.id });
      adapter.loadTourById(summary.id, resolvedLocale)
        .then((tour) => {
          setMode((m) =>
            // Only transition if the user hasn't navigated away or
            // selected a different tour while we were resolving.
            m.kind === 'loading' && m.tourId === tour.id
              ? { kind: 'playing', tour }
              : m,
          );
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[guide-me] failed to load tour', summary.id, err);
          setMode((m) =>
            m.kind === 'loading' && m.tourId === summary.id ? { kind: 'menu' } : m,
          );
        });
    },
    [adapter, resolvedLocale],
  );

  // Deep-link auto-start: an external link (e.g. the partner-training "Show me
  // in Experience" buttons) passes `?tour=<id>`. captureTourParam.ts stashes it
  // in sessionStorage at startup (surviving the auth/OIDC round-trip); we also
  // read a live `?tour=` as a fallback. loadTourById resolves any tour by id
  // from the global manifest, so this fires regardless of the current route.
  useEffect(() => {
    // Never fight a tour that's already resolving or running.
    if (mode.kind === 'loading' || mode.kind === 'playing') return;
    const id = readPendingTourId();
    if (!id) return;
    // Guard against a concurrent re-run of this effect (adapter identity changes
    // as auth/plugin context settles) starting the same load twice.
    if (deepLinkTourInFlight === id) return;
    deepLinkTourInFlight = id;
    // eslint-disable-next-line no-console
    console.info('[guide-me] auto-starting deep-link tour', id);
    setLogEntries([]);
    logEntriesRef.current = [];
    setMode({ kind: 'loading', tourId: id });
    adapter.loadTourById(id, resolvedLocale)
      .then((tour) => {
        // Clear the pointer only now that the tour has resolved and is about to
        // play — if a remount interrupted us earlier the pointer survived and the
        // next mount retried, instead of the tour being silently dropped.
        clearPendingTourId();
        try {
          const u = new URL(window.location.href);
          if (u.searchParams.has('tour')) {
            u.searchParams.delete('tour');
            window.history.replaceState({}, '', u.toString());
          }
        } catch { /* noop */ }
        // eslint-disable-next-line no-console
        console.info('[guide-me] deep-link tour playing', id);
        setMode((m) => (m.kind === 'loading' && m.tourId === tour.id ? { kind: 'playing', tour } : m));
      })
      .catch((err) => {
        // Hard failure (unknown id / parse error): clear so we don't loop, and
        // surface exactly what failed for diagnosis.
        clearPendingTourId();
        deepLinkTourInFlight = null;
        // eslint-disable-next-line no-console
        console.error('[guide-me] deep-link tour failed to load', id, err);
        setMode((m) => (m.kind === 'loading' ? { kind: 'idle' } : m));
      });
  }, [adapter, resolvedLocale, mode.kind]);

  const handleClose = useCallback(() => {
    setMode({ kind: 'idle' });
  }, []);

  const handleNarrate = useCallback((entry: TourLogEntry) => {
    const prev = logEntriesRef.current;
    const last = prev.length > 0 ? prev[prev.length - 1] : null;
    if (
      last &&
      last.message === entry.message &&
      last.stepType === entry.stepType &&
      !entry.terminalStatus
    ) {
      return;
    }
    const next = [...prev, entry];
    logEntriesRef.current = next;
    setLogEntries(next);
  }, []);

  const handleLogBack = useCallback(() => {
    setLogEntries([]);
    logEntriesRef.current = [];
    setMode({ kind: 'menu' });
  }, []);

  /* ─────────────────── render ─────────────────── */

  const hasMenu = !!guideEntry && tourSummaries.length > 0;
  const pillVisible = !!pillSummary || hasMenu;
  if (!pillVisible && mode.kind === 'idle') return null;

  // Synthesize a manifest-shaped summary for the pill when the active
  // plugin has no tour matching the current route. Pure metadata, no body.
  const pillSummarySafe: TourSummary =
    pillSummary ?? {
      id: '__menu__',
      pluginId: pluginId ?? '__none__',
      title: guideEntry?.pluginLabel ?? 'Guide Me',
      summary: 'See what you can do here',
      route: pathname,
      match: { route: pathname },
    };

  return (
    <>
      {mode.kind === 'idle' && !floatingLauncherDismissed && (
        <HintPill
          tour={pillSummarySafe}
          routeKey={routeKey}
          onLaunch={handleOpenMenu}
          onDismissLauncher={dismissFloatingLauncher}
          appLocale={resolvedLocale}
          variant="tour"
          messages={pillTeasers}
        />
      )}
      {mode.kind === 'menu' && (
        <GuideMenuPanel
          pluginLabel={guideEntry?.pluginLabel ?? 'This screen'}
          pluginIcon={guideEntry?.pluginIcon}
          tours={tourSummaries}
          pathname={pathname}
          pluginGuideUrl={pluginGuideUrl}
          onSelectTour={handleSelectTour}
          onClose={handleClose}
          appLocale={resolvedLocale}
        />
      )}
      {mode.kind === 'loading' && <LoadingShade />}
      {mode.kind === 'playing' && (
        <>
          <TourPlayer
            tour={mode.tour}
            onClose={handleClose}
            onNarrate={handleNarrate}
            sidebarWidth={340}
            appLocale={resolvedLocale}
          />
          <TourLogPanel
            tour={mode.tour}
            entries={logEntries}
            onBack={handleLogBack}
            onClose={handleClose}
            appLocale={resolvedLocale}
          />
        </>
      )}
    </>
  );
}

/**
 * Minimal full-screen shade shown for the brief moment between "user
 * clicked Play" and "tour body finished parsing". Most loads complete in
 * the same tick they're requested (the body was already preloaded by
 * trigger #1) — this shade only ever flashes for a tour the user happens
 * to play before the route preload finishes.
 */
function LoadingShade() {
  return (
    <div
      data-testid="guide-me-loading"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--modal-overlay, rgba(15, 23, 42, 0.45))',
        color: 'var(--main-text, white)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: 13,
        letterSpacing: '0.04em',
      }}
    >
      Loading tour…
    </div>
  );
}
